import { Table } from "@/components/v2/Table";
import { fetchServicepartnersWithoutPagination } from "@/lib/api/resources/servicePartners";
import { ServicePartner } from "@rumble-it/common-lib/types/models/ServicePartner.interface";
import { ServicePartnerCollection } from "@rumble-it/common-lib/types/models/ServicePartnerCollection.interface";
import { useQuery } from "@tanstack/react-query";
import { Card, Input } from "antd";
import { ColumnsType, TableRowSelection } from "antd/es/table/interface";
import { useMemo, useState } from "react";

// Shape shared by the brief endpoint (edit mode) and the collection detail (view mode).
type ServicePartnerRow = Pick<
  ServicePartner,
  "id" | "name" | "groupName" | "city"
>;

interface ServicePartnersCardProps {
  servicePartnerCollection?: ServicePartnerCollection;
  isLoading?: boolean;
  editMode?: boolean;
  selectedServicePartnerIds?: string[];
  setSelectedServicePartnerIds?: (servicePartnerIds: string[]) => void;
}

export const ServicePartnersCard = ({
  servicePartnerCollection,
  isLoading = false,
  editMode = false,
  selectedServicePartnerIds = [],
  setSelectedServicePartnerIds,
}: ServicePartnersCardProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const columns: ColumnsType<ServicePartnerRow> = [
    {
      title: "Naam",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Groep",
      dataIndex: "groupName",
      key: "groupName",
      sorter: (a, b) => (a.groupName ?? "").localeCompare(b.groupName ?? ""),
    },
    {
      title: "Plaats",
      dataIndex: "city",
      key: "city",
      sorter: (a, b) => (a.city ?? "").localeCompare(b.city ?? ""),
    },
  ];

  const servicePartnersQuery = useQuery({
    queryKey: ["servicePartners", "brief"],
    queryFn: () => fetchServicepartnersWithoutPagination(),
    enabled: editMode,
  });

  const filteredServicePartners = useMemo(
    () =>
      (editMode
        ? servicePartnersQuery.data
        : servicePartnerCollection?.servicePartners
      )?.filter((servicePartner) => {
        const term = searchTerm.toLowerCase();
        return (
          servicePartner.name?.toLowerCase().includes(term) ||
          (servicePartner.groupName?.toLowerCase().includes(term) ?? false) ||
          (servicePartner.city?.toLowerCase().includes(term) ?? false)
        );
      }),
    [editMode, searchTerm, servicePartnersQuery.data, servicePartnerCollection],
  );

  const rowSelection: TableRowSelection<ServicePartnerRow> | undefined =
    editMode
      ? {
          selectedRowKeys: selectedServicePartnerIds,
          preserveSelectedRowKeys: true,
          onChange: (selectedRowKeys: React.Key[]) => {
            setSelectedServicePartnerIds?.(selectedRowKeys as string[]);
          },
        }
      : undefined;

  return (
    <Card
      loading={isLoading || servicePartnersQuery.isLoading}
      className="w-full"
      title={
        "Herstellers" +
        (editMode ? ` (${selectedServicePartnerIds?.length}) geselecteerd` : "")
      }
    >
      <Input.Search
        placeholder="Zoek herstellers op naam, groep of plaats..."
        allowClear
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ marginBottom: 16 }}
      />

      <Table
        size="small"
        columns={columns}
        dataSource={filteredServicePartners}
        rowKey={(record) => record["id"]}
        rowSelection={rowSelection}
        pagination={false}
        scroll={{ y: 500 }}
      />
    </Card>
  );
};
